/**
 * Event Bus Service - Cross-app event communication via Central API
 *
 * Emits events when bookings are created/updated/canceled,
 * fetches inbox on app load for events from other apps (e.g., invoice.paid),
 * and marks events as read after processing.
 */

import apiService from './ApiService';

const APP_ID = 'booking-system';
const LAST_SYNC_KEY = `${APP_ID}_lastEventSync`;

const EventBusService = {
  /**
   * Emit an event to the central event bus (fire-and-forget)
   * @param {string} eventType - e.g. 'booking.created'
   * @param {Object} data - Event payload
   */
  async emitEvent(eventType, data) {
    try {
      await apiService.centralAPI.post('/events/emit', {
        eventType,
        appId: APP_ID,
        data
      });
    } catch (err) {
      console.warn('[EventBus] emit failed:', eventType, err.message);
    }
  },

  /**
   * Fetch inbox events for this app
   * @param {Object} options - { since?: ISO string }
   */
  async fetchInbox(options = {}) {
    const params = new URLSearchParams();
    if (options.since) params.append('since', options.since);
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await apiService.centralAPI.get(`/events/inbox/${APP_ID}${qs}`);
    return response.data?.events || response.data || [];
  },

  /**
   * Mark events as read
   * @param {string[]} eventIds
   */
  async markRead(eventIds) {
    if (!eventIds.length) return;
    await apiService.centralAPI.post(`/events/inbox/${APP_ID}/mark-read`, { eventIds });
  },

  /**
   * Sync inbox: fetch unread events since last sync, dispatch window events, mark read.
   * Silent — no toasts, just dispatches CustomEvents on window.
   */
  async syncInbox() {
    try {
      const since = localStorage.getItem(LAST_SYNC_KEY) || undefined;
      const events = await this.fetchInbox({ since });

      if (!events.length) {
        localStorage.setItem(LAST_SYNC_KEY, new Date().toISOString());
        return;
      }

      const idsToMark = [];

      for (const evt of events) {
        const type = evt.eventType || evt.type;
        if (!type) continue;
        idsToMark.push(evt.id || evt.eventId);

        // Dispatch window CustomEvents for silent consumption by components
        if (type.startsWith('invoice.')) {
          window.dispatchEvent(new CustomEvent('platform:invoice-paid', { detail: evt }));
        } else if (type.startsWith('subscription.')) {
          window.dispatchEvent(new CustomEvent('platform:subscription-changed', { detail: evt }));
        } else if (type.startsWith('payment.')) {
          window.dispatchEvent(new CustomEvent('platform:payment-update', { detail: evt }));
        }
        // All others: logged, not dispatched
      }

      const validIds = idsToMark.filter(Boolean);
      if (validIds.length) {
        await this.markRead(validIds).catch((err) =>
          console.warn('[EventBus] markRead failed:', err.message)
        );
      }

      localStorage.setItem(LAST_SYNC_KEY, new Date().toISOString());
    } catch (err) {
      console.warn('[EventBus] syncInbox failed:', err.message);
    }
  }
};

export default EventBusService;
